<template>
  <div class="bg-white dark:bg-gray-800 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
    <div class="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
      <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">
        Micronutrients
      </h3>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        B Vitamins & Minerals
      </p>
    </div>
    <div class="px-4 py-5 sm:p-6">
      <!-- Fat Breakdown Section -->
      <div v-if="hasFatBreakdown" class="mb-6">
        <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Fat Breakdown
        </h4>
        <div class="grid grid-cols-2 gap-4">
          <NutrientProgress
            v-if="fatBreakdown.saturated != null"
            label="Saturated Fat"
            :value="fatBreakdown.saturated"
            :target="targets.saturatedFat"
            unit="g" />
          <NutrientProgress
            v-if="fatBreakdown.monounsaturated != null"
            label="Monounsaturated Fat"
            :value="fatBreakdown.monounsaturated"
            unit="g" />
          <NutrientProgress
            v-if="fatBreakdown.polyunsaturated != null"
            label="Polyunsaturated Fat"
            :value="fatBreakdown.polyunsaturated"
            unit="g" />
          <NutrientProgress
            v-if="fatBreakdown.cholesterol != null"
            label="Cholesterol"
            :value="fatBreakdown.cholesterol"
            :target="targets.cholesterol"
            unit="mg" />
        </div>
      </div>

      <!-- B Vitamins Section -->
      <div v-if="hasBVitamins" class="mb-6">
        <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          B Vitamins
        </h4>
        <div class="grid grid-cols-2 gap-4">
          <NutrientProgress
            v-if="micronutrients.vitamin_b6 != null"
            label="Vitamin B6"
            :value="micronutrients.vitamin_b6"
            :target="targets.vitaminB6"
            unit="mg" />
          <NutrientProgress
            v-if="micronutrients.vitamin_b12 != null"
            label="Vitamin B12"
            :value="micronutrients.vitamin_b12"
            :target="targets.vitaminB12"
            unit="mcg" />
          <NutrientProgress
            v-if="micronutrients.folate != null"
            label="Folate"
            :value="micronutrients.folate"
            :target="targets.folate"
            unit="mcg" />
        </div>
      </div>

      <!-- Minerals Section -->
      <div v-if="hasMinerals">
        <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Minerals
        </h4>
        <div class="grid grid-cols-2 gap-4">
          <NutrientProgress
            v-if="micronutrients.magnesium != null"
            label="Magnesium"
            :value="micronutrients.magnesium"
            :target="targets.magnesium"
            unit="mg" />
          <NutrientProgress
            v-if="micronutrients.zinc != null"
            label="Zinc"
            :value="micronutrients.zinc"
            :target="targets.zinc"
            unit="mg" />
          <NutrientProgress
            v-if="micronutrients.phosphorus != null"
            label="Phosphorus"
            :value="micronutrients.phosphorus"
            :target="targets.phosphorus"
            unit="mg" />
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!hasAnyData" class="text-center py-8">
        <i class="fa-regular fa-capsules text-4xl text-gray-300 dark:text-gray-600 mb-3" />
        <p class="text-sm text-gray-500 dark:text-gray-400">
          No micronutrient data available
        </p>
        <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">
          Connect HealthKit to sync nutrition data
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import NutrientProgress from './nutrient_progress.vue'

const props = defineProps({
  fatBreakdown: {
    type: Object,
    default: () => ({})
  },
  micronutrients: {
    type: Object,
    default: () => ({})
  },
  targets: {
    type: Object,
    default: () => ({})
  }
})

const hasFatBreakdown = computed(() => {
  const fb = props.fatBreakdown
  return fb.saturated != null || fb.monounsaturated != null ||
         fb.polyunsaturated != null || fb.cholesterol != null
})

const hasBVitamins = computed(() => {
  const m = props.micronutrients
  return m.vitamin_b6 != null || m.vitamin_b12 != null || m.folate != null
})

const hasMinerals = computed(() => {
  const m = props.micronutrients
  return m.magnesium != null || m.zinc != null || m.phosphorus != null
})

const hasAnyData = computed(() => hasFatBreakdown.value || hasBVitamins.value || hasMinerals.value)
</script>
